import React, {
  useContext,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import fetch from 'isomorphic-unfetch';

import { ERROR_CODES } from './constants';
import { updateHistoryString } from './helpers';
import { GlobalsContext } from './context/globals';
import { StoreContext } from './store/StoreContext';
import { actionTypes } from './store/reducers';

import Results from './Results';
import Filters from './Filters';
import Error from './Error';


const App = ({ getGeoData }) => {
  const resultsRef = useRef();
  const globals = useContext(GlobalsContext);
  const store = useContext(StoreContext);
  const radiusCheck = store.state.location && store.state.radiusKm === 0
    ? globals.globals.defaultLocationRadiusValue
    : store.state.radiusKm;
  const resultsCacheKey = store.state.results.map(r => r.url).join('');

  function onUpdateSearchParam(key, value) {
    store.dispatch({
      type: actionTypes.UPDATE_SEARCH_PARAM,
      payload: {
        key,
        value,
        globals,
      },
    });
  }


  function onUpdateSearchParamAndSearch(key, value) {
    store.dispatch({
      type: actionTypes.UPDATE_SEARCH_PARAM_AND_SEARCH,
      payload: {
        key,
        value,
        globals,
      },
    });
  }


  function onReset() {
    // Reset state
    store.dispatch({
      type: actionTypes.RESET,
      payload: { globals },
    });
    // Reset url
    window.history.pushState(null, null, `${window.location.pathname}`);
  }


  function onPagination(event, page) {
    event.preventDefault();
    store.dispatch({
      type: actionTypes.PAGINATION,
      payload: {
        page,
        globals,
      },
    });
  }

  function onSearch(isInitialSearch = false, resetPage = true) {
    if (!isInitialSearch) {
      store.dispatch({ type: actionTypes.LOADING_START });

      getGeoData(store.state.location, ({ latitude, longitude }) => {
        store.dispatch({ type: actionTypes.LOADING_STOP });

        // Updating the radius state through dispatch if there is a location entered but no radius.
        // Ensures searches with location entered by user are local.
        store.dispatch({
          type: actionTypes.UPDATE_DEFAULT_LOCATION_RADIUS,
          payload: { radiusKm: radiusCheck },
        });
        // Updating params on search, passing in default location radius.
        // Location radius is taken from global state for this update
        // (defaultLocationRadius does not change, defined in CMS)
        // Using store.state from above dispatch doesn't update in time - led to race condition.
        updateHistoryString(store.state, radiusCheck);
        store.dispatch({
          type: actionTypes.SEARCH,
          payload: {
            globals,
            latitude,
            longitude,
            isInitialSearch,
            resetPage,
          },
        });
      });
    } else {
      store.dispatch({ type: actionTypes.SEARCH, payload: { globals, isInitialSearch } });
    }
  }


  function onPopState() {
    store.dispatch({ type: actionTypes.POP_STATE, payload: { globals } });
  }


  useEffect(() => {
    if (typeof store.state.apiQuery === 'string') {
      store.dispatch({ type: actionTypes.LOADING_START });
      fetch(store.state.apiQuery)
        .then(response => response.json())
        .then(({ count: totalResults, items: results, ...args }) => { // formatted for state
          if (args.ExceptionMessage) {
            store.dispatch({
              type: actionTypes.ERROR,
              payload: {
                code: ERROR_CODES[1],
                message: `There was a problem getting search results from the API: ${`ExceptionMessage:${args.ExceptionMessage} StackTrace:${args.StackTrace} `}`,
              },
            });
          } else if (totalResults === undefined || results === undefined) {
            store.dispatch({
              type: actionTypes.ERROR,
              payload: {
                code: ERROR_CODES[1],
                message: 'There was a problem getting search results from the API: unknown',
              },
            });
          } else {
            store.dispatch({ type: actionTypes.LOADING_STOP });
            store.dispatch({
              type: actionTypes.SEARCH_COMPLETE,
              payload: { results, totalResults },
            });
          }
        })
        .catch((e) => {
          store.dispatch({
            type: actionTypes.ERROR,
            payload: {
              code: ERROR_CODES[1],
              message: `There was a problem getting search results from the API: ${e.message}`,
            },
          });
        });
    }
  }, [store.state.apiQuery]);


  useEffect(() => {
    const isInitialSearch = true;
    const resetPage = false;
    onSearch(isInitialSearch, resetPage);
    window.addEventListener('popstate', onPopState);

    return () => window.removeEventListener('popstate', onPopState);
  }, []);


  // scroll to top of results upon results change
  useEffect(() => {
    if (resultsRef && resultsRef.current) {
      const bounds = resultsRef.current.getBoundingClientRect();
      if (bounds.top < 0) {
        resultsRef.current.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }
  }, [resultsCacheKey]);

  return (
    <div className="WhatsOnSearch_searchInner">
      {store.state.error ? (
        <Error code={store.state.error.code} message={store.state.error.message} />
      ) : (
        <>
          <Filters
            onUpdate={onUpdateSearchParam}
            onSearch={onSearch}
            onReset={onReset}
          />
          <Results
            onUpdate={onUpdateSearchParamAndSearch}
            onPagination={onPagination}
            ref={resultsRef}
          />
        </>
      )}
    </div>
  );
};


App.propTypes = {
  getGeoData: PropTypes.func,
};


export default App;
